import {Activity, ACTIVITY_DEFAULT_VALUES, calculateBestAvgSpeed, getFeelingId} from "services/ActivityService.js";
import CoordinateUtil from "utils/CoordinateUtil.js";
import DirectionUtil from "utils/DirectionUtil.js";
import {Point, calculateTimeAndDistanceOfPoints} from "services/PointService.js";
import DateTimeUtil from "utils/DateTimeUtil.js";
import {SETTING_DISTANCE_UNITS, SETTING_SPEED_UNITS} from "constants/AppConstants.js";
import {DbCollectionFactory} from "factories/DbCollectionFactory.js";
import {DB_COLLECTION_NAMES} from "constants/AppConstants.js";
import IdGeneratorUtil from "utils/IdGeneratorUtil.js";
import ConvertUtil from "utils/ConvertUtil.js";

const equipmentFactory: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.equip);

/**
 * Row payload sizes for VKX format
 * Based on: https://github.com/vakaros/vkx
 */
const ROW_PAYLOAD_SIZES: Record<number, number> = {
  0xff: 7, // Page Header
  0xfe: 2, // Page Terminator
  0x02: 44, // Position, Velocity, and Orientation
  0x03: 20, // Declination
  0x04: 13, // Race Timer Event
  0x05: 17, // Line Position
  0x06: 18, // Shift Angle
  0x08: 13, // Device Configuration
  0x0a: 16, // Wind
  0x0b: 16, // Speed Through Water
  0x0c: 12, // Depth
  0x10: 12, // Temperature
  0x0f: 16, // Load
  // Internal messages
  0x01: 32,
  0x07: 12,
  0x0e: 16,
  0x20: 13,
  0x21: 52
};

/**
 * Read little-endian signed 32-bit integer
 * @param {DataView} dataView - DataView to read from
 * @param {number} offset - Byte offset
 * @return {number} Signed 32-bit integer
 */
function readI4(dataView: DataView, offset: number): number {
  return dataView.getInt32(offset, true);
}

/**
 * Read little-endian unsigned 64-bit integer
 * @param {DataView} dataView - DataView to read from
 * @param {number} offset - Byte offset
 * @return {number} Unsigned 64-bit integer as Number
 */
function readU8(dataView: DataView, offset: number): number {
  // JavaScript doesn't have native 64-bit int support, so we need to handle it carefully
  // For timestamps, we'll read as two 32-bit integers
  const low = dataView.getUint32(offset, true);
  const high = dataView.getUint32(offset + 4, true);
  // Combine as Number (timestamps in milliseconds should fit in Number's safe integer range)
  return high * 0x100000000 + low;
}

/**
 * Read little-endian 32-bit float
 * @param {DataView} dataView - DataView to read from
 * @param {number} offset - Byte offset
 * @return {number} 32-bit float
 */
function readF4(dataView: DataView, offset: number): number {
  return dataView.getFloat32(offset, true);
}

/**
 * Read little-endian unsigned 8-bit integer
 * @param {DataView} dataView - DataView to read from
 * @param {number} offset - Byte offset
 * @return {number} Unsigned 8-bit integer
 */
function readU1(dataView: DataView, offset: number): number {
  return dataView.getUint8(offset);
}

export interface VkxParsedPoint {
  timestamp: number;
  latitude: number;
  longitude: number;
  speed: number;
  bearing: number;
  elevation: number;
  altitude: number;
}

/**
 * Parse VKX file and extract activity data
 * @param {ArrayBuffer} vkxBuffer - VKX file binary data
 * @param {string} userUid - User ID
 * @param {boolean} convert - Whether to convert to activity DB format
 * @return {Promise<Object>} Activity data and points DB
 */
export const importVkxFileToActivityDb = async (
  vkxBuffer: ArrayBuffer,
  userUid: string,
  convert: boolean = true
): Promise<any> => {
  const dataView = new DataView(vkxBuffer);
  let points: VkxParsedPoint[] = [];
  let offset = 0;

  // Parse rows
  while (offset < vkxBuffer.byteLength) {
    if (offset + 1 > vkxBuffer.byteLength) {
      break; // Need at least 1 byte for row key
    }

    const rowKey = readU1(dataView, offset);
    offset += 1;

    const payloadSize = ROW_PAYLOAD_SIZES[rowKey];
    if (payloadSize === undefined) {
      console.warn(`Unknown row key: 0x${rowKey.toString(16)} at offset ${offset - 1}`);
      // For unknown rows, try to scan ahead for the next known row key
      // Look ahead up to 100 bytes for a known row key
      let found = false;
      for (let scanOffset = offset; scanOffset < Math.min(offset + 100, vkxBuffer.byteLength); scanOffset++) {
        const potentialKey = readU1(dataView, scanOffset);
        if (ROW_PAYLOAD_SIZES[potentialKey] !== undefined) {
          offset = scanOffset; // Set to where we found the key (will read key in next iteration)
          found = true;
          break;
        }
      }
      if (!found) {
        break; // Can't find next known row, stop parsing
      }
      continue;
    }

    if (offset + payloadSize > vkxBuffer.byteLength) {
      break; // Not enough data for payload
    }

    // Handle Page Header (0xFF) - version info (we can read it but don't need it for parsing)
    if (rowKey === 0xff) {
      offset += payloadSize;
      continue;
    }

    // Handle Page Terminator (0xFE)
    if (rowKey === 0xfe) {
      offset += payloadSize;
      continue;
    }

    // Handle Position, Velocity, and Orientation (0x02)
    if (rowKey === 0x02 && payloadSize === 44) {
      const timestamp = readU8(dataView, offset); // Unix timestamp in milliseconds UTC
      const latitudeInt = readI4(dataView, offset + 8); // Latitude as I4 in 10^-7 degrees
      const longitudeInt = readI4(dataView, offset + 12); // Longitude as I4 in 10^-7 degrees
      const sog = readF4(dataView, offset + 16); // Speed Over Ground in m/s
      const cog = readF4(dataView, offset + 20); // Course Over Ground in radians
      const altitude = readF4(dataView, offset + 24); // Altitude in meters
      // Quaternions at offset + 28, 32, 36, 40 (we don't need them for basic activity)

      // Convert latitude/longitude from 10^-7 degrees to decimal degrees
      const latitude = latitudeInt / 10000000.0;
      const longitude = longitudeInt / 10000000.0;

      // Convert timestamp from milliseconds to seconds
      const timestampSeconds = Math.floor(timestamp / 1000);

      // Convert COG from radians to degrees
      const cogDegrees = (cog * 180) / Math.PI;

      // Only add point if we have valid coordinates
      if (!isNaN(latitude) && !isNaN(longitude) && latitude !== 0 && longitude !== 0) {
        points.push({
          timestamp: timestampSeconds,
          latitude,
          longitude,
          speed: sog >= 0 ? sog : 0, // Speed in m/s
          bearing: cogDegrees >= 0 && cogDegrees <= 360 ? cogDegrees : -1,
          elevation: !isNaN(altitude) ? altitude : 0,
          altitude
        });
      }
    }

    offset += payloadSize;
  }

  if (points.length === 0) {
    throw new Error("No position data found in VKX file");
  }

  // Sort points by timestamp
  points.sort((a, b) => a.timestamp - b.timestamp);

  // Deduplicate points based on timestamp, latitude, and longitude
  // Keep the point with more data (e.g., with bearing if available)
  const originalCount = points.length;
  const uniquePoints: VkxParsedPoint[] = [];
  const seenKeys = new Set<string>();

  points.forEach((point) => {
    const key = `${point.timestamp}-${point.latitude.toFixed(7)}-${point.longitude.toFixed(7)}`;

    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniquePoints.push(point);
    } else {
      // Found duplicate - check if this one has better bearing data
      const existingIndex = uniquePoints.findIndex(
        (p) =>
          p.timestamp === point.timestamp &&
          Math.abs(p.latitude - point.latitude) < 0.0000001 &&
          Math.abs(p.longitude - point.longitude) < 0.0000001
      );

      if (existingIndex >= 0) {
        const existing = uniquePoints[existingIndex];
        // If new point has valid bearing and existing doesn't, replace it
        if (point.bearing >= 0 && existing.bearing < 0) {
          uniquePoints[existingIndex] = point;
        }
      }
    }
  });

  points = uniquePoints;
  if (originalCount !== points.length) {
    console.log(
      `Deduplicated VKX points: ${points.length} unique from ${originalCount} (removed ${originalCount - points.length} duplicates)`
    );
  }

  // Keep only points with at least 1 second between consecutive points
  const MIN_POINT_INTERVAL_SECONDS = 1;
  const beforeIntervalFilter = points.length;
  const pointsOneSecondApart: VkxParsedPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    const lastKept = pointsOneSecondApart[pointsOneSecondApart.length - 1];
    if (!lastKept || pt.timestamp >= lastKept.timestamp + MIN_POINT_INTERVAL_SECONDS) {
      pointsOneSecondApart.push(pt);
    }
  }
  points = pointsOneSecondApart;
  if (beforeIntervalFilter !== points.length) {
    console.log(
      `VKX 1s interval filter: ${points.length} points from ${beforeIntervalFilter} (kept points with ≥${MIN_POINT_INTERVAL_SECONDS}s spacing)`
    );
  }

  // Convert to Point objects
  const pointObjects = points.map((pt, index) => {
    const prevPoint = index > 0 ? points[index - 1] : null;
    let bearing = pt.bearing;

    // Calculate bearing from previous point if not available
    if (bearing < 0 && prevPoint) {
      bearing = DirectionUtil.getBearingValue(prevPoint.latitude, prevPoint.longitude, pt.latitude, pt.longitude);
    }

    return new Point({
      a: null, // accuracy
      c: 0, // calories
      h: 0, // heart rate
      l: pt.latitude,
      n: pt.longitude,
      r: bearing >= 0 ? bearing : -1,
      p: 0, // pause time
      s: pt.speed, // speed in m/s
      e: pt.elevation, // elevation
      t: pt.timestamp, // timestamp in seconds
      m: `point-${index}`
    });
  });

  // Create activity
  const activity = new Activity({}, null);
  activity.points = pointObjects;
  activity.name = "";
  activity.typeId = ACTIVITY_DEFAULT_VALUES.typeId; // Default one
  activity.waterSportId = 1; // Sailing
  activity.feelingId = getFeelingId(ACTIVITY_DEFAULT_VALUES.feelingId); // Default one
  activity.fnt = "VKX";
  activity.startDateS = points[0]?.timestamp || ACTIVITY_DEFAULT_VALUES.durationInUnixTimestamp;
  activity.originalDateInUTC = DateTimeUtil.getDateByTimeStamp(activity.startDateS);
  activity.pausedTimeInSeconds = 0;
  activity.durationInSeconds =
    points[points.length - 1]?.timestamp - points[0]?.timestamp || ACTIVITY_DEFAULT_VALUES.durationInSeconds;
  activity.isDeleted = false;
  activity.isSaved = true;

  // Calculate time and distance for each point
  const computedPoints = calculateTimeAndDistanceOfPoints(pointObjects);
  activity.points = computedPoints;

  // Calculate total distance from the last point
  activity.totalDistanceInKm =
    computedPoints.length > 0 ? computedPoints[computedPoints.length - 1].distanceFromStartPointInMeter / 1000 : 0;

  // Calculate average speed (km/h) = distance (km) / time (hours)
  activity.averageSpeedInKM =
    activity.durationInSeconds > 0 ? activity.totalDistanceInKm / (activity.durationInSeconds / 3600) : 0;

  // Calculate maximum speed (km/h) from points
  activity.maxSpeedInKM =
    computedPoints.length > 0
      ? ConvertUtil.convertMPSToKMH(
          computedPoints.reduce((acc, point) => {
            const speedInMPS = point.speedInMPS || 0;
            return Math.max(acc, speedInMPS);
          }, 0)
        )
      : 0;

  activity.bestSpeedFiveSecondsKM = calculateBestAvgSpeed(pointObjects, 5, 0, SETTING_SPEED_UNITS.km_slash_h)[0];
  const coords = activity.getCoordsFromPoints(activity.points);
  activity.linearDistance = CoordinateUtil.calculateLinearDistance(coords, SETTING_DISTANCE_UNITS.kilometers);
  activity.latitude = activity.points?.[0]?.latitude;
  activity.longitude = activity.points?.[0]?.longitude;

  // Get wind and location information
  const getLocAndWindData = async () => {
    await activity.getWindInformation().catch(() => {});
    await activity.getLocationInformation().catch(() => {});
  };

  if (typeof process !== "undefined" && typeof process.env !== "undefined" && process.env.REACT_APP_FIREBASE_API_KEY) {
    // @ts-ignore -- /src is outside the node tsconfig program; resolved by the web bundler at runtime
    const {isAndroid} = await import("/src/FirebaseUtils.js");
    const NetworkModule = await import("@capacitor/network");
    const Network = NetworkModule.Network;

    if (isAndroid) {
      const status = await Network.getStatus();
      if (status.connected) {
        await getLocAndWindData();
      }
    } else {
      await getLocAndWindData();
    }
  } else {
    await getLocAndWindData();
  }

  activity.id = IdGeneratorUtil.generateUuid();

  // Get equipment
  let equipments: any = await equipmentFactory.getAllEquipsByUser(
    userUid,
    null,
    {
      field: "sport",
      value: activity.waterSportId
    },
    3,
    false,
    undefined
  );
  equipments = equipments
    ? Object.values(equipments)?.filter((equipment: any) => equipment.sport === activity.waterSportId)
    : [];
  activity.equipment1 = equipments?.[0]?.sport || "";
  activity.equipment2 = equipments?.[1]?.sport || "";
  activity.equipment3 = equipments?.[2]?.sport || "";

  if (convert) {
    return activity.convertActivityToActivityDbAndPointsDb(userUid);
  }

  return activity;
};
